import React from 'react';
import { useHistory } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Api from '../../../api';
import TalentNavbar from '../../../components/TalentNavbar';
import TalentBackButton from '../../../components/TalentBackButton/TalentBackButton';
import MembershipBannerMessage from '../../../components/MembershipBannerMessage';
import { Analytics } from 'utils/analytics';
import { getMembershipBannerCopy } from '../../../utils/membership';
import type { CourseListItem, MembershipStatusResponse } from '../../../utils/types';
import { CeDirectory, useCeDirectoryAvailability } from '../../../features/ceDirectory';
import styles from './CoursesHomePage.module.scss';

const LEVEL_ONE_STATUS_CODES = new Set(['RL1', 'FL1', 'RL1FL1']);

export function getSoftRecommendationBody(membership: MembershipStatusResponse | null): string {
  if (membership?.currentStatusCode && LEVEL_ONE_STATUS_CODES.has(membership.currentStatusCode)) {
    return 'No course is required for your current status. This optional course is recommended to help strengthen your standing.';
  }

  return 'Based on your current standing, this course is the fastest path to re-earning full shift access.';
}

// T2-6475: derive the banner/recommendation state (and whether to show the
// 24h course-completion timing note) from the membership payload. Extracted so
// the gating rules are unit-testable without rendering the whole page.
export function deriveCourseBannerState(membership: MembershipStatusResponse | null) {
  const overlayActive = !!membership?.overlay?.active;
  const overlayState = membership?.overlay?.state ?? null;
  const isCourseRequiredRecovery = overlayState === 'course_required'
    || membership?.statusCategory === 'ffs_recovery_course_required'
    || membership?.statusCategory === 'provisional_course_required'
    || membership?.currentStatusCode === 'CourseRequired';
  const isCourseRequired = membership?.mode === 'enforced' && isCourseRequiredRecovery;
  const recommendedCourse = membership?.recommendedCourse ?? null;
  const requiredCourses = membership?.recommendedCourses?.length
    ? membership.recommendedCourses
    : recommendedCourse
      ? [recommendedCourse]
      : [];
  const recommendedLmsIds = new Set(
    requiredCourses
      .map(course => course.lmsCourseId)
      .filter((id): id is number => id != null)
  );
  // A soft-recommendation shows the recommended-course banner without
  // blocking other courses. Fires when the user has a recommendation but no
  // hard overlay (e.g. RL1/FL1 guidance path).
  const hasSoftRecommendation = !isCourseRequiredRecovery && !overlayActive && !!recommendedCourse;
  // The required (overlay) banner renders whenever there is an active overlay;
  // the timing note appears for either the required or recommended banner.
  const hasRequiredBanner = !!membership && overlayActive;
  const showCompletionTimingNote = hasRequiredBanner || (hasSoftRecommendation && !!recommendedCourse);
  return {
    overlayActive,
    isCourseRequiredRecovery,
    isCourseRequired,
    recommendedCourse,
    requiredCourses,
    recommendedLmsIds,
    hasSoftRecommendation,
    showCompletionTimingNote,
  };
}

const CoursesHomePage: React.FC = () => {
  const history = useHistory();
  const handleBack = React.useCallback(() => {
    history.push({ pathname: 'hygienistHome' });
  }, [history]);
  const [courses, setCourses] = React.useState<CourseListItem[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [embedOk, setEmbedOk] = React.useState<boolean>(true);
  const [embedMessage, setEmbedMessage] = React.useState<string>('');
  const [membership, setMembership] = React.useState<MembershipStatusResponse | null>(null);
  const [membershipLoadFailed, setMembershipLoadFailed] = React.useState(false);
  const [analyticsEmitted, setAnalyticsEmitted] = React.useState<boolean>(false);
  const [firstPartyExpanded, setFirstPartyExpanded] = React.useState(false);
  const [loadAttempt, setLoadAttempt] = React.useState(0);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    Api.updateToken()
      .then(async () => {
        try {
          const health = await Api.getCoursesEmbedHealth();
          if (mounted) {
            const ok = !!(health.customDomain && health.noXFO && health.frameAncestorsOK && health.cookiesOK);
            setEmbedOk(ok);
            setEmbedMessage(health.message);
          }
        } catch (e) {
          // ignore health failure; default embedOk=true
        }
        // Load courses and membership status in parallel. Membership failure
        // must not block the courses list from rendering, but course-list
        // failure should surface only the first-party error/retry state.
        const [coursesData, membershipResult] = await Promise.all([
          Api.getCourses(),
          Api.getMembershipStatus()
            .then(data => ({ data, failed: false }))
            .catch(() => ({ data: null as MembershipStatusResponse | null, failed: true })),
        ]);
        if (mounted) {
          setCourses(coursesData);
          setMembership(membershipResult.data);
          setMembershipLoadFailed(membershipResult.failed);
        }
      })
      .catch((e: any) => { if (mounted) setError(typeof e === 'string' ? e : 'Failed to load courses'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [loadAttempt]);

  // Emit a single `Courses Home Loaded` analytics event once the page is
  // fully hydrated with a (possibly null) membership and courses payload so
  // Product can see under which overlay state Talent actually open the page.
  React.useEffect(() => {
    if (loading) return;
    if (analyticsEmitted) return;
    Analytics.track('Courses Home Loaded', {
      membershipOverlayState: membership?.overlay?.state ?? null,
      membershipOverlayActive: membership?.overlay?.active ?? false,
      membershipOverlaySource: membership?.overlay?.source ?? null,
      membershipMode: membership?.mode ?? null,
      membershipStatusCategory: membership?.statusCategory ?? null,
      recommendedCourseLmsId: membership?.recommendedCourse?.lmsCourseId ?? null,
      recommendedCourseCode: membership?.recommendedCourse?.code ?? null,
      coursesAvailable: courses.length,
      platform: (window as any).cordova ? 'mobile' : 'web',
    });
    setAnalyticsEmitted(true);
  }, [loading, analyticsEmitted, membership, courses.length]);

  const handleStart = async (lmsCourseId: number) => {
    try {
      setLoading(true);
      await Api.updateToken();
      history.push({ pathname: 'coursePlayer', state: { lmsCourseId } });
    } catch (e) {
      setError('Failed to start course');
    } finally {
      setLoading(false);
    }
  };

  const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
  const hygienist = currentUser?.hygienist;
  const firstName = hygienist?.firstName || '';
  const lastName = hygienist?.lastName || '';
  const photoUrl = hygienist?.photoUrl || undefined;
  const globalUserId = currentUser?.id;
  const ceDirectoryEnabled = useCeDirectoryAvailability(globalUserId) === true;
  const isHygOrAsst = hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.HYGIENIST ||
                      hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.ASSISTANT;

  const {
    overlayActive,
    isCourseRequiredRecovery,
    isCourseRequired,
    recommendedCourse,
    requiredCourses,
    recommendedLmsIds,
    hasSoftRecommendation,
    showCompletionTimingNote,
  } = deriveCourseBannerState(membership);

  const getProgressBadgeClass = (course: CourseListItem) => {
    if (course.passedFinalQuiz) return styles.passed;
    if (course.isCompleted) return styles.completed;
    if (course.progressPercent != null && course.progressPercent > 0) return styles.inProgress;
    return styles.notStarted;
  };

  const getProgressText = (course: CourseListItem) => {
    if (course.passedFinalQuiz) return '✓ Passed';
    if (course.isCompleted) return '✓ Completed';
    if (course.progressPercent != null && course.progressPercent > 0) return `${course.progressPercent}% Complete`;
    return 'Not Started';
  };

  const renderCoursesList = (courseItems: CourseListItem[]) => courseItems.length === 0 ? (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon} aria-hidden="true">
        <SchoolIcon />
      </div>
      <div className={styles.emptyTitle}>No Courses Available</div>
      <div className={styles.emptyMessage}>
        Check back soon for new learning opportunities!
      </div>
    </div>
  ) : (
    <ul className={styles.coursesList}>
      {courseItems.map(c => {
        const isAssignedCourse = !!c.isAssignedRecoveryCourse || recommendedLmsIds.has(c.lmsCourseId);
        const isRecommendedCourse = hasSoftRecommendation && recommendedLmsIds.has(c.lmsCourseId);
        // In enforced course_required state with an assigned course,
        // disable unrelated courses so talent don't grind an unrelated
        // course and expect their overlay to clear. Shadow mode remains
        // guidance-only and keeps all course actions available.
        const isBlockedUnrelated = isCourseRequired && recommendedLmsIds.size > 0 && !isAssignedCourse;
        return (
          <li
            key={c.lmsCourseId}
            data-lms-course-id={c.lmsCourseId}
            className={`${styles.courseCard} ${isAssignedCourse ? styles.assignedCourseCard : ''}`}
          >
            <div className={styles.courseHeader}>
              <h3 className={styles.courseTitle}>
                {c.title}
                {isAssignedCourse && (
                  <span className={styles.assignedBadge}>
                    {isRecommendedCourse ? 'Recommended' : 'Assigned'}
                  </span>
                )}
              </h3>
              <span className={`${styles.progressBadge} ${getProgressBadgeClass(c)}`}>
                {getProgressText(c)}
              </span>
            </div>
            <div className={styles.courseDescription}>
              {c.progressPercent != null && c.progressPercent > 0
                ? `Continue where you left off - ${c.progressPercent}% complete`
                : 'Start your learning journey with this course'}
            </div>
            <div className={styles.courseAction}>
              <button
                className={styles.startButton}
                onClick={() => handleStart(c.lmsCourseId)}
                disabled={!embedOk || isBlockedUnrelated}
              >
                {c.progressPercent != null && c.progressPercent > 0 ? 'Continue Learning' : 'Start Course'}
              </button>
              {isBlockedUnrelated && (
                <div className={styles.assignedBlockedCaption}>
                  Complete your assigned course to restore access.
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );

  const firstPartyState = loading ? (
    <div className={styles.loadingState}>
      <div className={styles.loadingSpinner}></div>
      <div className={styles.loadingText}>Loading courses...</div>
      <div className={styles.loadingSubtext}>Please wait a moment</div>
    </div>
  ) : error ? (
    <div className={styles.errorState}>
      <div className={styles.errorIcon} aria-hidden="true"><ErrorOutlineIcon /></div>
      <div className={styles.errorTitle}>Unable to Load Courses</div>
      <div className={styles.errorMessage}>{error}</div>
      <button className={styles.retryButton} onClick={() => setLoadAttempt(attempt => attempt + 1)}>
        Try Again
      </button>
    </div>
  ) : null;

  const overlayBanner = membership && overlayActive
    ? getMembershipBannerCopy(membership, { firstName })
    : null;
  const assignedCourses = courses.filter(course => (
    !!course.isAssignedRecoveryCourse || recommendedLmsIds.has(course.lmsCourseId)
  ));
  const otherCourses = courses.filter(course => (
    !course.isAssignedRecoveryCourse && !recommendedLmsIds.has(course.lmsCourseId)
  ));
  const shouldPinAssignedCourses = isCourseRequiredRecovery || assignedCourses.length > 0;
  const directoryFirstPartyCourses = shouldPinAssignedCourses
    ? [...assignedCourses, ...otherCourses]
    : courses;
  const useCollapsedTempStarsRow = ceDirectoryEnabled
    && !membershipLoadFailed
    && membership !== null
    && assignedCourses.length === 0
    && !overlayActive
    && !hasSoftRecommendation
    && !isCourseRequiredRecovery;

  return (
    <div className={styles.coursesPage}>
      <TalentNavbar
        pageTitle={ceDirectoryEnabled ? 'CE Courses' : undefined}
        firstName={firstName}
        lastName={lastName}
        photoUrl={photoUrl}
        isHygOrAsst={isHygOrAsst}
        instabookActivated={hygienist?.InstabookActivated === 1}
        isTestAccount={(hygienist as any)?.isTest === 1}
        showAddShiftButton={false}
        showJobBoardButton={false}
      />
      <TalentBackButton onBack={handleBack} />

      <div className={styles.container}>
        {firstPartyState || (
          <>
            {/* Overlay-aware required-course banner. Shows when the user has an
                active course_required / restricted_reentry / not_a_fit overlay. */}
            {overlayBanner && (
              <div
                className={`${styles.requiredCourseBanner} ${
                  overlayBanner.tone === 'error'
                    ? styles.requiredCourseBannerError
                    : overlayBanner.tone === 'warning'
                      ? styles.requiredCourseBannerWarning
                      : styles.requiredCourseBannerInfo
                }`}
              >
                <div className={styles.requiredCourseBannerTitle}>{overlayBanner.title}</div>
                <div className={styles.requiredCourseBannerBody}>
                  <MembershipBannerMessage copy={overlayBanner} />
                </div>
                {recommendedCourse && (
                  <div className={styles.requiredCourseBannerCourse}>
                    Assigned course{requiredCourses.length > 1 ? 's' : ''}: <strong>{requiredCourses.map(course => course.title).join(', ')}</strong>
                  </div>
                )}
              </div>
            )}

            {/* Softer recommendation banner: user has a recommended course but no
                hard overlay (e.g. RL1/FL1 guidance). Does not block anything. */}
            {hasSoftRecommendation && recommendedCourse && (
              <div className={`${styles.requiredCourseBanner} ${styles.requiredCourseBannerInfo}`}>
                <div className={styles.requiredCourseBannerTitle}>Recommended next course</div>
                <div className={styles.requiredCourseBannerBody}>
                  {getSoftRecommendationBody(membership)}
                </div>
                <div className={styles.requiredCourseBannerCourse}>
                  <strong>{recommendedCourse.title}</strong>
                </div>
              </div>
            )}

            {/* T2-6475: subtle one-line note that course completion can take up to
                24h to reflect. Only shown when a required (overlay) or recommended
                course banner is present — never for plain optional-course users. */}
            {showCompletionTimingNote && (
              <p className={styles.completionTimingNote}>
                Please note, once you complete a course it may take up to 24h for your account to reflect it.
              </p>
            )}

            {/* Warning if embedding not ready */}
            {!embedOk && (
              <div className={styles.warningCard}>
                <div className={styles.warningIcon} aria-hidden="true">
                  <WarningAmberIcon />
                </div>
                <div className={styles.warningContent}>
                  <div className={styles.warningTitle}>Courses Temporarily Unavailable</div>
                  <div className={styles.warningMessage}>
                    {embedMessage || 'Courses cannot be displayed in-app right now. Please try again later or contact support.'}
                  </div>
                </div>
              </div>
            )}

            {useCollapsedTempStarsRow ? (
              <section className={styles.tempStarsCoursesSection} aria-labelledby="tempstars-courses-heading">
                <button
                  type="button"
                  className={styles.tempStarsCoursesToggle}
                  onClick={() => setFirstPartyExpanded(expanded => !expanded)}
                  aria-expanded={firstPartyExpanded}
                  aria-controls="tempstars-courses-list"
                >
                  <span id="tempstars-courses-heading">Your TempStars courses ({courses.length} available)</span>
                  <span aria-hidden="true">{firstPartyExpanded ? '−' : '+'}</span>
                </button>
                {firstPartyExpanded && (
                  <div id="tempstars-courses-list">
                    {renderCoursesList(courses)}
                  </div>
                )}
              </section>
            ) : (
              renderCoursesList(ceDirectoryEnabled ? directoryFirstPartyCourses : courses)
            )}
          </>
        )}
        {ceDirectoryEnabled && globalUserId != null && (
          <CeDirectory
            userId={globalUserId}
            sourceSurface="talent"
          />
        )}
      </div>
    </div>
  );
};

export default CoursesHomePage;
